import React, { useState } from "react"
import { Link } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { URL } from "../../store/actions/depannageActions"
import { handleErrors } from "../../store/actions/apiHelpers"
import { COLORS } from "../../utilities/Constants"
import { generalStyles } from "../styles/styles"
import CustomModal from "../basics/interface/CustomModal"
import ClientAutocomplete from "../agenda/ClientAutocomplete"
import {
	ENTITE_TYPE_PERSONNE_PHYSIQUE,
	ENTITE_TYPE_SOCIETE,
} from "../dashboard/entite/Entite"
import { postEntite, postEntiteReset } from "../../store/actions/entiteActions"
import { getOpenerId } from "../../utilities/session"
import {
	clientInvalide,
	clientVersFormData,
	nouveauClient,
} from "../dashboard/clients/clientCreation"

// GE-67 (Jordan 10.10) : fiche « issue d'un dépannage » — fournitures vendues
// par le dépanneur, rattachées au client de passage. Bandeau avec le lien
// vers le dépannage, et la bascule vers un vrai client tant que la fiche est
// en client de passage (l'entité partagée n'est jamais modifiée).
export const BandeauIssueDepannage = ({
	issue,
	enCours,
	peutBasculer,
	onBasculer,
}) =>
	issue.type === "reparation" ? (
		<BandeauReparation
			issue={issue}
			peutBasculer={peutBasculer}
			onBasculer={onBasculer}
		/>
	) : (
		<div style={st.bandeau}>
			{enCours && (
				<strong style={{ width: "100%" }}>
					Fournitures en cours de saisie par le dépanneur : modifiable
					après la clôture de la mission.
				</strong>
			)}
			<span>
				Issue d'un dépannage : fournitures vendues par le dépanneur
				(mission M{issue.mission_id}
				{issue.plaques ? ` · ${issue.plaques}` : ""}).
			</span>
			{issue.dep_id ? (
				<Link to={`/depannage/fiche/${issue.dep_id}`} style={st.lien}>
					Fiche dépannage N° {issue.dep_id}
				</Link>
			) : (
				<span style={{ color: COLORS.textMuted }}>
					Mission pas encore convertie en fiche dépannage.
				</span>
			)}
			{issue.client_de_passage && peutBasculer && (
				<button
					type="button"
					style={generalStyles.btnSecondary}
					onClick={onBasculer}
				>
					Associer un client
				</button>
			)}
		</div>
	)

const joindre = (parts, sep = " ") => parts.filter(Boolean).join(sep)

const LienDossier = ({ issue }) =>
	issue.dep_id ? (
		<Link to={`/depannage/fiche/${issue.dep_id}`} style={st.lien}>
			Fiche dépannage N° {issue.dep_id}
		</Link>
	) : (
		<span style={{ color: COLORS.textMuted }}>
			Mission pas encore convertie en fiche dépannage.
		</span>
	)

// GE-69 : fiche de RÉPARATION créée au départ du lieu vers un POI Atelier.
// Site (atelier de destination), véhicule et contact recopiés de la mission
// (instantané figé : survit à la purge et à un changement de destination).
const BandeauReparation = ({ issue, peutBasculer, onBasculer }) => {
	const site = issue.site || {}
	const v = issue.vehicule || {}
	const c = issue.contact || {}
	const vehicule = joindre([v.marque, v.modele, v.plaques])
	const contact = joindre([joindre([c.prenom, c.nom]), c.telephone], " · ")
	return (
		<div style={st.bandeau}>
			{issue.n_arrive_plus && (
				<strong style={{ width: "100%", color: COLORS.danger }}>
					Le véhicule n'arrive plus à l'atelier (destination changée
					ou mission annulée) : fiche gardée car déjà travaillée — à
					vérifier.
				</strong>
			)}
			<span>
				{/* Lot 9 (Jordan 30.10) : « Atelier », plus « Réparation ». */}
				Atelier — site <strong>{site.label || "—"}</strong>
				{site.adresse ? ` (${site.adresse})` : ""}. Mission M
				{issue.mission_id}
				{vehicule ? ` · ${vehicule}` : ""}
				{contact ? ` · ${contact}` : ""}.
			</span>
			<LienDossier issue={issue} />
			{issue.client_de_passage && peutBasculer && (
				<button
					type="button"
					style={generalStyles.btnSecondary}
					onClick={onBasculer}
				>
					Associer un client
				</button>
			)}
		</div>
	)
}

// Revue 8 (Fa2) : badge des listes atelier (Contrôle avant facturation,
// résultats de recherche) — fiche des fournitures d'une mission.
export const BadgeIssueDepannage = ({ issue }) =>
	issue ? (
		<span
			style={st.badge}
			title={
				issue.type === "reparation"
					? `Atelier — site ${(issue.site || {}).label || "—"}, mission M${issue.mission_id}${issue.plaques ? ` (${issue.plaques})` : ""}`
					: `Fournitures vendues par le dépanneur — mission M${issue.mission_id}${issue.plaques ? ` (${issue.plaques})` : ""}`
			}
		>
			{issue.type === "reparation" ? "Atelier" : "Dépannage"}
		</span>
	) : null

// Modal « Associer un client » (toute fiche en client de passage, GE-67 et
// vente rapide). Client EXISTANT (recherche) ou NOUVEAU client créé ici même
// (règles et endpoint partagés avec /clients/nouveau : clientCreation.js) puis
// associé en un geste. Bouton déjà dans un modal → bascule « Confirmer ? »
// (convention V2), pas de second modal.
export const ModalBasculeClient = ({
	atelierId,
	travailNonEnregistre,
	onClose,
	onFait,
	// GE-69 : contact recopié de la mission ({nom, prenom, telephone}) —
	// pré-remplit « Nouveau client » (personne).
	contact = null,
}) => {
	const dispatch = useDispatch()
	const { token } = useSelector((s) => s.navInfo.login)
	const [mode, setMode] = useState("existant") // existant | nouveau
	const [texte, setTexte] = useState("")
	const [choisi, setChoisi] = useState(null)
	const [client, setClient] = useState(() => ({
		...nouveauClient(ENTITE_TYPE_PERSONNE_PHYSIQUE),
		...(contact
			? {
					nom: contact.nom || "",
					prenom: contact.prenom || "",
					telephone: contact.telephone || "",
				}
			: {}),
	}))
	const [confirmer, setConfirmer] = useState(false)
	const [etat, setEtat] = useState({ loading: false, erreur: "" })

	const associer = (entite) =>
		fetch(`${URL}api/atelier/bascule-client`, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				id: atelierId,
				entite_id: entite.id,
				// Revue 8 (Fa14) : la session doit détenir la fiche.
				opener: getOpenerId(),
			}),
		}).then(handleErrors)

	const echec = (prefixe) => (err) =>
		setEtat({
			loading: false,
			erreur: `${prefixe}${(err && err.message) || "l'opération a échoué."}`,
		})

	const lancer = () => {
		setEtat({ loading: true, erreur: "" })
		if (mode === "existant")
			return associer(choisi)
				.then(() => onFait())
				.catch(echec("Association refusée : "))
		// Nouveau client : création (même endpoint que /clients/nouveau), puis
		// association. Si la 2e étape échoue, le client créé reste choisi : un
		// nouvel essai n'associe que. Sans l'adresse vierge de la base : elle
		// serait reprise telle quelle sur la fiche (règle « une seule adresse »).
		const fd = clientVersFormData({ ...client, adresses: [] })
		return dispatch(postEntite(fd)).then((cree) => {
			if (!cree || !cree.id) {
				// Erreur lue dans le store APRÈS l'échec (catchApiError normalise
				// {message}) ; état remis à zéro pour un nouvel essai.
				const e = dispatch(
					(_, getState) => getState().entiteInfo.postEntite.error,
				)
				dispatch(postEntiteReset())
				setEtat({
					loading: false,
					erreur: `Création du client refusée : ${(e && e.message) || "vérifiez les champs."}`,
				})
				return
			}
			dispatch(postEntiteReset())
			setChoisi(cree)
			setTexte(cree.label || `${cree.nom} ${cree.prenom}`)
			setMode("existant")
			return associer(cree)
				.then(() => onFait())
				.catch(
					echec(
						"Client créé, mais l'association a échoué (réessayez « Associer ») : ",
					),
				)
		})
	}

	const pret =
		!travailNonEnregistre &&
		(mode === "existant" ? !!choisi : !clientInvalide(client))
	const libelle = mode === "existant" ? "Associer" : "Créer et associer"
	const changer = (cle, valeur) => {
		setConfirmer(false)
		setClient((c) => ({ ...c, [cle]: valeur }))
	}
	const societe = client.type === ENTITE_TYPE_SOCIETE
	const champ = (cle, label) => (
		<div style={generalStyles.fieldRow}>
			<label style={generalStyles.label}>{label}</label>
			<input
				style={generalStyles.input}
				value={client[cle] || ""}
				onChange={(e) => changer(cle, e.target.value)}
			/>
		</div>
	)

	return (
		<CustomModal
			title="Associer un client"
			subtitle="La fiche quitte le client de passage pour un vrai client. Le client de passage lui-même n'est pas modifié."
			onClose={onClose}
			showCloseButton={false}
			buttons={[
				{
					title: "Annuler",
					styleName: "secondary",
					onClick: onClose,
					disabled: etat.loading,
				},
				{
					title: confirmer ? "Confirmer ?" : libelle,
					styleName: "primary",
					onClick: () => (confirmer ? lancer() : setConfirmer(true)),
					loading: etat.loading,
					disabled: !pret,
				},
			]}
		>
			<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
				{travailNonEnregistre && (
					<div style={{ fontSize: 13, color: COLORS.danger }}>
						Enregistrez d'abord la fiche : elle sera rechargée après
						l'association.
					</div>
				)}
				<div style={st.pastilles}>
					{[
						["existant", "Client existant"],
						["nouveau", "Nouveau client"],
					].map(([m, l]) => (
						<button
							key={m}
							type="button"
							style={st.pastille(mode === m)}
							onClick={() => {
								setMode(m)
								setConfirmer(false)
							}}
						>
							{l}
						</button>
					))}
				</div>
				{mode === "existant" ? (
					// Réserve la place de la liste (240 px max, absolue) : sinon
					// le corps du modal défile et rogne les pastilles.
					<div style={{ minHeight: texte && !choisi ? 290 : 0 }}>
						<ClientAutocomplete
							value={texte}
							onChange={(v) => {
								setTexte(v)
								setChoisi(null)
								setConfirmer(false)
							}}
							onSelect={(e) => {
								setChoisi(e)
								setTexte(e.label || e.display || "")
								setConfirmer(false)
							}}
							selected={!!choisi}
							placeholder="Rechercher un client"
						/>
					</div>
				) : (
					<>
						<div style={st.pastilles}>
							{[
								[ENTITE_TYPE_PERSONNE_PHYSIQUE, "Personne"],
								[ENTITE_TYPE_SOCIETE, "Société"],
							].map(([t, l]) => (
								<button
									key={t}
									type="button"
									style={st.pastille(client.type === t)}
									onClick={() => changer("type", t)}
								>
									{l}
								</button>
							))}
						</div>
						{societe ? (
							champ("label", "Raison sociale *")
						) : (
							<>
								{champ("nom", "Nom *")}
								{champ("prenom", "Prénom *")}
							</>
						)}
						{champ("telephone", "Téléphone *")}
						<div style={{ fontSize: 12, color: COLORS.textMuted }}>
							Le reste (adresse, véhicules…) se complète ensuite
							dans la fiche du client.
						</div>
					</>
				)}
				{etat.erreur && (
					<div style={{ fontSize: 13, color: COLORS.danger }}>
						{etat.erreur}
					</div>
				)}
			</div>
		</CustomModal>
	)
}

const st = {
	bandeau: {
		display: "flex",
		alignItems: "center",
		flexWrap: "wrap",
		gap: 12,
		marginBottom: 16,
		padding: "10px 14px",
		borderRadius: 10,
		background: COLORS.accentLight,
		border: `1px solid ${COLORS.border}`,
		color: COLORS.text,
		fontSize: 13,
	},
	lien: { color: COLORS.accent, fontWeight: 600 },
	pastilles: { display: "flex", gap: 6, flexWrap: "wrap" },
	pastille: (actif) => ({
		border: `1px solid ${actif ? COLORS.accent : COLORS.border}`,
		background: actif ? COLORS.accentLight : COLORS.surface,
		color: COLORS.text,
		borderRadius: 99,
		padding: "5px 12px",
		fontSize: 13,
		cursor: "pointer",
		fontFamily: "inherit",
	}),
	badge: {
		marginLeft: 8,
		padding: "1px 8px",
		borderRadius: 99,
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.accent,
		background: COLORS.accentLight,
		border: `1px solid ${COLORS.border}`,
		whiteSpace: "nowrap",
	},
}
